import forms from '../../../resources/forms.json';
import { FormLayoutBuilder, getFormData } from '@pihealth/common-ui-library';
import { Alert, Box, Button } from '@mui/material';
import AppModal from '../../modal';
import { useRef, useState } from 'react';
import { useAppSelector } from '../../../store';
import { useUtil } from '../../hooks/useUtil';
import DownloadIcon from '@mui/icons-material/Download';
import apiConfig from 'src/api/APIConfig';
import { endPoints } from 'src/api/APIEndpoints';
import { sendRequest } from 'src/hooks/useFetch';
import { useGetStudyDetailsQuery } from 'src/services/DrugSupply';
import moment from 'moment';
import { SERVER_DATE_FORMAT } from 'src/constants/formats';

export interface IImportCSVDrugSupplyProps {
  setImportCSVPopup: (data: boolean) => void;
  importCSVPopup: boolean;
  setCSVRecords: (data: any) => void;
  setManualEntryPopup: (data: any) => void;
}

const ImportCSVDrugSupply = (props: IImportCSVDrugSupplyProps) => {
  const { importCSVPopup, setImportCSVPopup, setCSVRecords, setManualEntryPopup } = props;
  const [saveDisable, setSaveDisable] = useState(true);
  const [loading, setLoading] = useState(false);
  const importDrugSupplyGroup = forms.importCSVDrugSupply;
  const formRef: any = useRef(null);
  const user: any = useAppSelector(state => state.user.user);
  const { showError, showSuccess, handleError } = useUtil();
  const { formFields, defaultValues = {}, rules } = getFormData(importDrugSupplyGroup.groups);
  const selectedStudy: string | null = useAppSelector(state => state.site.selectedStudy);
  const { data: getStudyDetailsData, isSuccess: getStudyDetailsIsSuccess } = useGetStudyDetailsQuery({
    study_id: selectedStudy,
    application: 'SYSADMIN',
    application_section: 'SYSADMIN',
    application_module_section: 'Master List'
  });

  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const { groups } = formFields;
  const ValidateData = (data: any) => {
    if (!getStudyDetailsIsSuccess) {
      showError('Input file template mismatch');
      return false;
    }
    const investigational_products_details = getStudyDetailsData.study_investigational_products_details.find(
      (ele: any) => ele.custom_name === data?.[0]?.custom_name
    );
    const Arr = data
      .map((record: any) => {
        if (!record.dose) {
          record.dose = investigational_products_details?.dose;
        }
        if (!record.dose_unit) {
          record.dose_unit = investigational_products_details?.unit;
        }
        if (!record.cost_per_unit) {
          record.cost_per_unit = investigational_products_details?.cost;
        }
        if (!record.investigational_drug) {
          record.investigational_drug = investigational_products_details?.investigational_drug;
        }

        if (!record.earliest_expiration_date) {
          record.earliest_expiration_date = null;
        } else if (!moment(record.earliest_expiration_date, 'D/M/YYYY', true).isValid()) {
          return;
        } else {
          record.earliest_expiration_date = moment(record.earliest_expiration_date, 'D/M/YYYY').format(
            SERVER_DATE_FORMAT
          );
        }
        if (!record.latest_expiration_date) {
          record.latest_expiration_date = null;
        } else if (!moment(record.latest_expiration_date, 'D/M/YYYY', true).isValid()) {
          return;
        } else {
          record.latest_expiration_date = moment(record.latest_expiration_date, 'D/M/YYYY').format(SERVER_DATE_FORMAT);
        }
        if (Number(record.quantity_retained) < 0 || Number(record.quantity_retained) > 99999) {
          return;
        }
        if (
          isNaN(Number(record.quantity)) ||
          isNaN(Number(record.quantity_retained)) ||
          isNaN(Number(record.number_of_kits)) ||
          isNaN(Number(record.cost_per_unit))
        ) {
          return;
        }
        if (
          record.latest_expiration_date &&
          record.earliest_expiration_date &&
          (moment(record.earliest_expiration_date, 'D/M/YYYY').isAfter(
            moment(record.latest_expiration_date, 'D/M/YYYY')
          ) ||
            record.latest_expiration_date === record.earliest_expiration_date)
        ) {
          return;
        }
        if (
          record.dose === investigational_products_details?.dose &&
          record.dose_unit === investigational_products_details?.unit &&
          record.cost_per_unit == investigational_products_details?.cost &&
          record.investigational_drug === investigational_products_details?.investigational_drug
        ) {
          return {
            ...record,
            quantity: Number(record.quantity),
            cost_currency: investigational_products_details.cost_unit,
            currency: investigational_products_details.cost_unit,
            number_of_kits: Number(record.number_of_kits),
            cost_per_unit: Number(record.cost_per_unit),
            isNew: false
          };
        }
      })
      .filter((ele: any) => ele);
    if (Arr?.length) {
      setCSVRecords(Arr);
    } else {
      showError('Input file template mismatch');
      return false;
    }
    return true;
  };

  const onImportFormSubmit = async (data: any) => {
    if (data.errors) {
      showError('Missing mandatory data');
      return;
    }
    if (data.success) {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('file', data?.records?.import_file[0]);
        const options = {
          pathVariables: {
            study_id: studyId
          },
          study_id: studyId,
          data: formData,
          accessToken: user?.token,
          sponsor: user.sponsor
        };
        const records: any = await sendRequest('upload_drupsupply_file', options);
        if (records.drug_supply_csv_data.length) {
          const Validated = ValidateData(records.drug_supply_csv_data);
          if (!Validated) {
            return;
          }
        }
        showSuccess('file import successful');
      } catch (e: any) {
        if (e?.response?.status == '422') {
          showError(e?.response?.data?.context?.message || 'Input file template mismatch');
        } else {
          handleError(e?.response);
        }
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    handleImportClose();
    setManualEntryPopup(true);
  };
  const handleImportSubmit = () => {
    formRef.current.submit(onImportFormSubmit);
  };
  const handleImportClose = () => {
    setImportCSVPopup(false);
    setSaveDisable(true);
  };

  const handlefileUploadFileChange = (key: string, value: any) => {
    if (value.length && value?.[0]?.path && key === 'import_file' && Array.isArray(value)) {
      showSuccess(`Your file ${value?.[0]?.path} has been successfully uploaded.`);
      setLoading(false);
      setSaveDisable(false);
    } else if (key === 'import_file' && value?.length) {
      showError(value);
    }
  };

  return (
    <AppModal
      open={importCSVPopup}
      title={importDrugSupplyGroup.header}
      handleSubmit={handleImportSubmit}
      handleClose={handleImportClose}
      outSideClickClose
      disabled={saveDisable}
      loading={loading}
    >
      <Box sx={{ mt: 1, '& .uploadFile > .MuiBox-root p.MuiTypography-h5': { left: 0, mt: '80px !important' } }}>
        <FormLayoutBuilder
          initialValues={defaultValues}
          rules={rules}
          ref={formRef}
          groups={groups}
          onChange={handlefileUploadFileChange}
        />
      </Box>
      <Alert severity="info" sx={{ mt: 0.5 }}>
        Document uploaded here are not stored in eTMF
      </Alert>
      <Alert severity="warning" sx={{ my: 1 }}>
        <strong>Note:</strong> Uploaded file should match downloaded template.
      </Alert>
      <Box sx={{ marginBottom: '-48px', mt: 1.5 }}>
        <a
          id="DrugsupplyTamplete"
          href={`${apiConfig.appConfig.apiutils}${endPoints.drugSupply.templateDownload}`} // Need to update with Import CSV file link
          download={'DrugsupplyTamplete'}
        >
          <Button variant="outlined" startIcon={<DownloadIcon />}>
            Download Template
          </Button>
        </a>
      </Box>
    </AppModal>
  );
};
export default ImportCSVDrugSupply;
